import * as React from "react";
import { Checkbox } from "@utdanningsdirektoratet/checkbox";
import { cls } from "@utdanningsdirektoratet/utils/cls";
import type { MultiSelectOption } from "./MultiSelect";
import styles from "./MultiSelect.module.less";

type OptionsProps<T> = {
  options: Array<MultiSelectOption<T>>;
  disabled?: boolean;
  onChange: (valuesToAdd: Array<T>, valuesToRemove: Array<T>) => void;
  values: Array<T>;
  groupValue?: T;
  groupIndex?: string;
  groupTitle?: string;
  search: string;
  focusedRef: React.RefObject<HTMLInputElement> | null;
  focusedIndex: string | null;
  id: string;
};

const Options = <T,>({
  id,
  options = [],
  search,
  focusedIndex,
  focusedRef,
  onChange,
  disabled,
  values,
  groupValue,
  groupIndex,
  groupTitle,
}: OptionsProps<T>): React.ReactElement => {
  return (
    <>
      {options
        .filter(
          (option) =>
            !search ||
            (groupTitle &&
              groupTitle.toLowerCase().indexOf(search.toLowerCase()) !== -1) ||
            option.text.toLowerCase().indexOf(search.toLowerCase()) !== -1,
        )
        .map((option, index) => {
          const optionClass = cls({
            [styles.multiSelectOption]: true,
            [styles.multiSelectOptionSelected]:
              focusedIndex === `${groupIndex ? `${groupIndex}-` : ""}${index}`,
          });
          const key = option.key ? option.key : `${option.value}`;
          return (
            <div key={key} className={optionClass}>
              <Checkbox
                id={`${id}-${groupIndex ? `${groupIndex}-` : ""}${index}`}
                disabled={disabled || option.disabled}
                ref={
                  focusedIndex ===
                  `${groupIndex ? `${groupIndex}-` : ""}${index}`
                    ? focusedRef
                    : null
                }
                label={option.text}
                tabIndex={-1}
                onChange={(e) => {
                  const lastOption =
                    options.filter((o) => values.indexOf(o.value) !== -1)
                      .length === 1;
                  onChange(
                    e.target.checked
                      ? [option.value]
                      : [...(lastOption && groupValue ? [groupValue] : [])],
                    e.target.checked
                      ? [...(groupValue ? [groupValue] : [])]
                      : [option.value],
                  );
                }}
                aria-selected={values.indexOf(option.value) !== -1}
                role="option"
                checked={values.indexOf(option.value) !== -1}
              />
            </div>
          );
        })}
    </>
  );
};

export default Options;
